import { select, takeEvery, call, put, takeLatest } from "redux-saga/effects";
import history from "./history";

import { startSubmit, stopSubmit } from "redux-form";
import {
  LOGIN_REQUEST,
  loginSuccess,
  LOGOUT_REQUEST,
  logoutSuccess,
  LOAD_DATA_REQUEST,
  loadDataSuccess,
  FORGOT_PASSWORD_REQUEST,
  RESET_PASSWORD_REQUEST,
} from "./actions";
import * as api from "./api";
import { getCurrentTenant } from "components/Utils/Utils";
import { setCookie } from "./helpers/Cookie";
import { identifyUser } from "./utils/analytics";

const getItem = window.localStorage.getItem.bind(window.localStorage);
const setItem = window.localStorage.setItem.bind(window.localStorage);
const removeItem = window.localStorage.removeItem.bind(window.localStorage);

function* startupSaga() {
  const token = yield call(getItem, "token");
  const tenant = getItem("defaultTenantSlug");

  let isCurrentTenant = false;
  const path = window.location.pathname;

  // Determine if a user is logged in to the same tenant
  if (path) {
    if (tenant === getCurrentTenant(path.split("/")[1]) || path === "/") {
      isCurrentTenant = true;
    } else {
      isCurrentTenant = false;
    }
  }

  if (token && isCurrentTenant) {
    yield put(loginSuccess(token));
  }
}

function* loginSaga() {
  yield takeLatest(
    LOGIN_REQUEST,
    function* ({ payload: { email, password, nextPathname } }) {
      yield put(startSubmit("login"));
      const response = yield call(api.post, "/auth/login", {
        data: { email, password },
      });

      if (response.status === 200) {
        const { token, user, roles } = yield response.json();

        const userName = user.firstName;
        const role = roles.map((v) => v.name);

        let defaultTenant;
        let next;

        const tenantSlugURL = nextPathname
          ? nextPathname.pathname.split("/")[1]
          : "/";

        if (tenantSlugURL) {
          defaultTenant = user.tenants.find((tenant) => {
            return tenant.slug === getCurrentTenant(tenantSlugURL)
              ? tenant
              : tenant.projects.find(
                  (project) => project.slug === tenantSlugURL
                );
          });

          next = nextPathname;

          if (!defaultTenant) {
            defaultTenant = user.tenants.find(
              (tenant) => tenant.slug === user.tenant
            );
            next = "/";
          }
        } else {
          defaultTenant = user.tenants.find(
            (tenant) => tenant.slug === user.tenant
          );
          next = "/";
        }

        yield put(loginSuccess(token, user));
        yield call(setItem, "token", token);
        yield call(setItem, "user", userName);
        yield call(setItem, "email", email);
        yield call(setItem, "roles", role);
        yield call(setItem, "defaultTenantID", defaultTenant.id);
        yield call(setItem, "defaultTenantSlug", defaultTenant.slug);

        // Store complete user object for analytics
        const analyticsUser = {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          tenant: defaultTenant.slug,
        };
        yield call(setItem, "currentUser", JSON.stringify(analyticsUser));

        yield call([history, history.push], next);

        identifyUser(analyticsUser);
      } else {
        const { error } = yield response.json();

        yield put(stopSubmit("login", { _error: error }));
      }
    }
  );
}

function* logoutSaga() {
  yield takeLatest(LOGOUT_REQUEST, function* () {
    clearSession();
    yield put(logoutSuccess());
    yield call([history, history.push], "/login");
  });
}

export function clearSession() {
  setCookie("token", null, -1);
  removeItem("token");
  removeItem("user");
  removeItem("roles");
  removeItem("permissions");
  removeItem("email");
  removeItem("defaultTenantID");
  removeItem("defaultTenantSlug");
  removeItem("hideAlerts");

  // Reset analytics identification
  import("./utils/analytics").then(({ resetIdentification }) => {
    resetIdentification();
  });
}

function* forgotPasswordSaga() {
  yield takeLatest(FORGOT_PASSWORD_REQUEST, function* ({ payload: { email } }) {
    yield put(startSubmit("forgot_password"));
    const response = yield call(api.post, "/auth/forgot_password", {
      data: { email },
    });

    if (response.status === 200) {
      const { message } = yield response.json();

      yield put(stopSubmit("forgot_password", { _error: message }));
    } else {
      const { message } = yield response.json();
      yield put(stopSubmit("forgot_password", { _error: message }));
    }
  });
}

function* resetPasswordSaga() {
  yield takeLatest(
    RESET_PASSWORD_REQUEST,
    function* ({ payload: { token, password } }) {
      yield put(startSubmit("reset_password"));
      const response = yield call(api.post, "/auth/password_reset", {
        data: { token, password },
      });

      if (response.status === 200) {
        const { message } = yield response.json();

        yield put(stopSubmit("reset_password", { _error: message }));

        yield call([history, history.push], "/");
      } else {
        const { message } = yield response.json();
        yield put(stopSubmit("reset_password", { _error: message }));
      }
    }
  );
}

function getToken() {
  return select((state) => state.auth || localStorage.token);
}

function* loadDataSaga() {
  yield takeEvery(LOAD_DATA_REQUEST, function* ({ payload: { url } }) {
    const token = yield getToken();
    const response = yield call(api.get, url, token);

    if (response.status === 200) {
      const data = yield response.json();
      yield put(loadDataSuccess({ data, url }));
    }
  });
}

const Actions = [
  startupSaga,
  loginSaga,
  logoutSaga,
  loadDataSaga,
  forgotPasswordSaga,
  resetPasswordSaga,
];

export default Actions;
